import React, { useMemo, useState } from 'react';
// logic
import { mutate } from 'swr';
import ticketService from 'modules/emergencies/ticketService';
// components
import { FormButton } from '../ui/buttons/FormButton';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import {
  ModalContainer,
  CloseButtonContainer,
  ModalBody,
  ModalFooter,
  ModalFormContainer,
  ModalHeader,
  ModalHeaderInnerContainer,
  ModalHeaderMainText,
  ModalHeaderSecondaryText,
  ModalBoldText,
} from './modalComponents';
import { PremiumInput } from '../ui/inputs/customInput';
import { SectionLine } from '../ticketForm/components/SectionLine';
import BasicLoader from '../ui/loaders/basicLoader';
import styled from 'styled-components';
import useCarrierusersFilter from '../ticketForm/useCarrierUsersFilter';

const WhiteBasicLoader = styled(BasicLoader)`
  & > div > div {
    background-color: white;
  }
`;

const t = {
  confirm: 'Add bid',
  back: 'Back',
  title: 'Add bid for carrier',
  carrierInputText: 'Carrier',
  amountInputText: 'Cost',
  currencyInputText: 'Currency',
  conditionsInputText: 'Conditions',
  backendError: 'There was an error creating the bid',
};
export const AddBidModal = ({ ok, cancel, addBidModalProps }) => {
  const { ticketDetail = {} } = addBidModalProps;

  const goBack = (e) => {
    e.preventDefault();
    cancel();
  };
  const formattedTicketId = ticketService.utils.getFormatedId(ticketDetail.id);

  const initialCarrierQuery = '';

  const { carrierOptions, carrierOptionsError, isLoadingCarrierOptions, setSearch } =
    useCarrierusersFilter({
      initialQuery: initialCarrierQuery,
    });

  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [carrierId, setCarrierId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [conditions, setConditions] = useState('');

  const hasCarrierError = useMemo(() => {
    return carrierId === '';
  }, [carrierId]);

  const hasAmountError = useMemo(() => {
    try {
      return amount === '' || isNaN(amount);
    } catch (e) {
      return true;
    }
  }, [amount]);

  const hasErrors = hasCarrierError || hasAmountError;

  const [hasBackendError, setHasBackendError] = useState(false);

  const onSubmit = async () => {
    setShowErrors(true);
    if (hasErrors) {
      return;
    }

    setIsWaitingForResponse(true);

    const addBidDto = {
      cost: {
        amount,
        currency,
      },
      conditions,
      bidder_id: carrierId,
    };
    const auctionId = ticketService.bussiness.getFirstOpenAuction(ticketDetail).id;

    try {
      await ticketService.callBackend.addBid({ addBidDto, auctionId });
      mutate((key) => {
        return typeof key === 'string' && key.startsWith('bids/ticket/');
      });
      ok({});
    } catch (e) {
      setIsWaitingForResponse(false);
      setHasBackendError(true);
    }
  };

  if (hasBackendError) {
    return (
      <ModalContainer>
        <ModalBody>
          <ModalHeader>
            <ModalHeaderInnerContainer>
              <ModalHeaderSecondaryText>{`Supply info - Ticket ID. ${formattedTicketId}`}</ModalHeaderSecondaryText>
              <ModalHeaderMainText>{t.title}</ModalHeaderMainText>
            </ModalHeaderInnerContainer>
          </ModalHeader>
          <ModalFormContainer>
            <SectionLine>
              <ModalBoldText>{t.backendError}</ModalBoldText>
            </SectionLine>
          </ModalFormContainer>
        </ModalBody>
        <ModalFooter>
          <FormButton variant="ghost" onClick={goBack}>
            {t.back}
          </FormButton>
        </ModalFooter>
        <CloseButtonContainer onClick={goBack}>
          <CloseIcon />
        </CloseButtonContainer>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer>
      <ModalBody>
        <ModalHeader>
          <ModalHeaderInnerContainer>
            <ModalHeaderSecondaryText>{`Supply info - Ticket ID. ${formattedTicketId}`}</ModalHeaderSecondaryText>
            <ModalHeaderMainText>{t.title}</ModalHeaderMainText>
          </ModalHeaderInnerContainer>
        </ModalHeader>
        <ModalFormContainer>
          <SectionLine>
            <PremiumInput
              onSearchChange={setSearch}
              onFieldChange={(selectedCarrierOption) => {
                setCarrierId(selectedCarrierOption);
              }}
              options={carrierOptions}
              isLoadingOptions={isLoadingCarrierOptions}
              customHasError={carrierOptionsError}
              initialValue={initialCarrierQuery}
              isRequired={true}
              isDisabled={isWaitingForResponse}
              value={carrierId}
              hasError={showErrors && hasCarrierError}
              label={t.carrierInputText}
              name="add_bid_carrier"
              type="select-with-search"
              searchLabel={'Search carrier'}
              optionsEmptyMssg={'No carriers found'}
              isClereable={true}
            />
          </SectionLine>
          <SectionLine>
            <PremiumInput
              type="number"
              label={t.amountInputText}
              name="add_bid_amount"
              value={amount}
              onChange={(value) => setAmount(value)}
              isRequired={true}
              isDisabled={isWaitingForResponse}
              hasError={showErrors && hasAmountError}
            />
            <PremiumInput
              label={t.currencyInputText}
              name="add_bid_currency"
              type={'select'}
              value={currency}
              isRequired={true}
              isDisabled={isWaitingForResponse}
              di
              onChange={(value) => {
                setCurrency(value);
              }}
              availableOptions={[
                {
                  value: 'EUR',
                  label: 'Euro',
                },
                {
                  value: 'MAD',
                  label: 'Dirham',
                },
              ]}
            />
          </SectionLine>
          <SectionLine>
            <PremiumInput
              type="text"
              label={t.conditionsInputText}
              name="add_bid_conditions"
              value={conditions}
              onChange={(event) => setConditions(event.target.value)}
              isDisabled={isWaitingForResponse}
            />
          </SectionLine>
        </ModalFormContainer>
      </ModalBody>
      <ModalFooter>
        <FormButton variant="ghost" onClick={goBack}>
          {t.back}
        </FormButton>
        <FormButton
          isDisabled={isWaitingForResponse || (showErrors && hasErrors)}
          onClick={() => onSubmit()}
        >
          {isWaitingForResponse ? <WhiteBasicLoader size={16}></WhiteBasicLoader> : t.confirm}
        </FormButton>
      </ModalFooter>
      <CloseButtonContainer onClick={goBack}>
        <CloseIcon />
      </CloseButtonContainer>
    </ModalContainer>
  );
};
